import React, { ReactNode } from 'react';
import { CoreAuthProvider, useCoreAuth } from './CoreAuthContext';
import { UserProvider, useUser } from './UserContext';
import { SubscriptionProvider, useSubscription } from './SubscriptionContext';

// Combined provider for backward compatibility
export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <CoreAuthProvider>
      <UserProvider>
        <SubscriptionProvider>
          {children}
        </SubscriptionProvider>
      </UserProvider>
    </CoreAuthProvider>
  );
}

// Combined hook for backward compatibility
export function useAuth() {
  const coreAuth = useCoreAuth();
  const userContext = useUser();
  const subscription = useSubscription();

  // Combined loading state - true if auth is loading OR profile is loading OR initial load hasn't completed
  const isLoading = coreAuth.isLoading || userContext.isLoadingProfile || (coreAuth.session && !userContext.hasInitialLoad);

  return {
    // From CoreAuth
    session: coreAuth.session,
    isLoading,
    login: coreAuth.login,
    logout: coreAuth.logout,
    loginWithGoogle: coreAuth.loginWithGoogle,
    resetPassword: coreAuth.resetPassword,
    resendEmailConfirmation: coreAuth.resendEmailConfirmation,
    
    // From UserContext
    user: userContext.user,
    hasTutorialCompleted: userContext.hasTutorialCompleted,
    markTutorialCompleted: userContext.markTutorialCompleted,
    hasOnboardingCompleted: userContext.hasOnboardingCompleted,
    markOnboardingCompleted: userContext.markOnboardingCompleted,
    
    // From SubscriptionContext
    hasSubscriptionPlan: subscription.hasSubscriptionPlan,
    updateSubscriptionPlan: subscription.updateSubscriptionPlan,
    updateCredits: subscription.updateCredits,
    refreshUserCredits: subscription.refreshUserCredits,
    
    // Session management
    refreshSession: coreAuth.refreshSession,
    
    // Register function with metadata mapping
    register: async (email: string, password: string, name: string, referralCode?: string, termsAccepted = false, marketingConsent = false) => {
      const now = new Date().toISOString();

      // Register the user - returns user data
      const user = await coreAuth.register(email, password, {
        name,
        referral_code: referralCode || null,
        terms_accepted_at: termsAccepted ? now : null,
        privacy_accepted_at: termsAccepted ? now : null
      });

      // After successful registration, handle marketing consent
      if (user && user.id) {
        try {
          const { supabase } = await import('../lib/supabase');
          console.log(`✅ Registration successful for user ${user.id}`);

          // Create email_preferences and sync to Resend via edge function (bypasses RLS)
          // During registration, user has no active session yet, so direct DB insert would fail RLS
          const { error: prefError } = await supabase.functions.invoke('manage-resend-contact', {
            body: {
              action: 'create-with-preferences',
              userId: user.id,
              marketingConsent,
              email,
              name
            }
          });

          if (prefError) {
            console.error('❌ Failed to create email preferences via edge function:', prefError);
            // Fallback: try direct insert (might fail due to RLS)
            console.log('⚠️ Attempting direct database insert as fallback...');
            const { error: directError } = await supabase
              .from('email_preferences')
              .upsert({
                user_id: user.id,
                marketing_emails: marketingConsent,
                project_notifications: true,
                usage_alerts: true,
                activity_digest: false,
                updated_at: now
              });

            if (directError) {
              console.error('❌ Direct insert also failed (expected due to RLS):', directError);
            }
          } else {
            console.log(`✅ Email preferences created: marketing_emails=${marketingConsent}`);
          }
        } catch (error) {
          console.error('❌ Error handling marketing consent:', error);
          // Don't throw - user registration was successful
        }
      } else {
        console.warn('⚠️ No user data returned from registration');
      }
    }
  };
}

// Export individual hooks for more granular access
export { useCoreAuth, useUser, useSubscription };